/**
 * useScreenReaderAnnouncements
 * 
 * Custom hook for WCAG 2.1 Level AA compliant screen reader announcements.
 * Handles aria-live region updates for chat message states.
 * 
 * Features:
 * - Announces processing, streaming, and completion states
 * - Strips markdown for clean screen reader output
 * - Reads full response text automatically
 * - Includes source and follow-up counts in announcements
 * - Brand-configurable announcement texts
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getBrandConfig } from '../brands/brand-registry';
import type { Message } from '../api/models';

// Types for screen reader configuration
interface ScreenReaderTexts {
  userAsks: string;
  processing: string;
  streaming: string;
  responseSays: string;
  withSources: string;
  withFollowUps: string;
  responseComplete: string;
}

interface UseScreenReaderAnnouncementsReturn {
  ariaLiveMessage: string;
  announceCustomMessage: (message: string) => void;
}

// ============================================================================
// Text Processing Utilities
// ============================================================================

/**
 * Strip markdown syntax for clean screen reader output.
 * Prevents reading "[link](url)" syntax literally.
 */
const stripMarkdownForSR = (text: string): string => {
  return text
    // Convert [text](url) to just "text"
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove standalone URLs
    .replace(/https?:\/\/[^\s]+/g, '')
    // Remove bold/italic markers
    .replace(/[*_]{1,2}([^*_]+)[*_]{1,2}/g, '$1')
    // Remove code blocks
    .replace(/`([^`]+)`/g, '$1')
    // Collapse multiple spaces
    .replace(/\s+/g, ' ')
    .trim();
};

// ============================================================================
// Main Hook
// ============================================================================

export const useScreenReaderAnnouncements = (
  messages: Message[]
): UseScreenReaderAnnouncementsReturn => {
  const brandConfig = getBrandConfig();
  
  // State for the aria-live region content
  const [ariaLiveMessage, setAriaLiveMessage] = useState('');
  
  // Refs for tracking announcement state
  const timeoutRef = useRef<number | null>(null);
  const lastAnnouncedMessageId = useRef<string | null>(null);
  const lastAnnouncedUserMessageId = useRef<string | null>(null);
  const streamingAnnouncedRef = useRef<boolean>(false);
  
  // Get localized screen reader texts (from brand config or defaults)
  const srTexts: ScreenReaderTexts = brandConfig.screenReader || {
    userAsks: 'User asks:',
    processing: `${brandConfig.appName} is searching for an answer...`,
    streaming: `${brandConfig.appName} is typing a response...`,
    responseSays: `${brandConfig.appName} says:`,
    withSources: 'with {count} source references',
    withFollowUps: '{count} follow-up questions available',
    responseComplete: 'Response complete.',
  };

  // ============================================================================
  // Announcement Builders
  // ============================================================================

  /**
   * Build the complete response announcement with metadata.
   */
  const buildCompleteAnnouncement = useCallback((
    responseText: string,
    citations: unknown[],
    followUps: string[]
  ): string => {
    const parts: string[] = [];
    
    // Clean markdown and use full response text
    const cleanText = stripMarkdownForSR(responseText);
    parts.push(`${srTexts.responseSays} ${cleanText}`);
    
    // Add source count if available
    if (citations.length > 0) {
      parts.push(srTexts.withSources.replace('{count}', citations.length.toString()));
    }
    
    // Add follow-up count if available
    if (followUps.length > 0) {
      parts.push(srTexts.withFollowUps.replace('{count}', followUps.length.toString()));
    }
    
    // Add completion indicator
    parts.push(srTexts.responseComplete);
    
    return parts.join('. ');
  }, [srTexts]);

  /**
   * Set announcement with auto-clear timeout.
   */
  const setAnnouncementWithTimeout = useCallback((
    message: string, 
    clearAfterMs: number = 5000
  ) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    
    setAriaLiveMessage(message);
    
    // Auto-clear after specified duration
    timeoutRef.current = window.setTimeout(() => {
      setAriaLiveMessage('');
    }, clearAfterMs);
  }, []);

  /**
   * Public method to announce custom messages (e.g., errors, actions).
   */
  const announceCustomMessage = useCallback((message: string) => {
    setAnnouncementWithTimeout(message, 3000);
  }, [setAnnouncementWithTimeout]);

  // ============================================================================
  // Message State Handler
  // ============================================================================

  useEffect(() => {
    // Clear announcements on initial load (WCAG 4.1.3 - no unnecessary announcements)
    if (messages.length === 0) {
      setAriaLiveMessage('');
      streamingAnnouncedRef.current = false;
      lastAnnouncedMessageId.current = null;
      lastAnnouncedUserMessageId.current = null;
      return;
    }
    
    const lastMessage = messages[messages.length - 1];
    
    // Handle user messages: Announce "User asks: [question]"
    if (lastMessage.role === 'user') {
      const userMessageId = lastMessage.uiMessageId;
      const userQuestion = lastMessage.englishText || '';
      
      // Only announce if we haven't announced this user message yet
      if (lastAnnouncedUserMessageId.current !== userMessageId && userQuestion) {
        lastAnnouncedUserMessageId.current = userMessageId;
        const userAnnouncement = `${srTexts.userAsks} ${userQuestion}`;
        setAnnouncementWithTimeout(userAnnouncement, 3000);
        // Note: Processing/streaming status will be announced automatically when assistant message appears
      }
      return;
    }
    
    // Handle assistant messages
    if (lastMessage.role !== 'assistant') return;
    
    const messageId = lastMessage.uiMessageId;
    const responseText = lastMessage.englishText || '';
    const citations = lastMessage.citations || [];
    const followUps = lastMessage.followUpQuestions || [];
    
    // State 1: Processing (thinking, not yet streaming)
    if (lastMessage.isThinking && !lastMessage.isStreaming) {
      setAriaLiveMessage(srTexts.processing);
      streamingAnnouncedRef.current = false;
      return;
    }
    
    // State 2: Streaming started (announce once)
    if (lastMessage.isStreaming && !streamingAnnouncedRef.current) {
      setAriaLiveMessage(srTexts.streaming);
      streamingAnnouncedRef.current = true;
      return;
    }
    
    // State 3: Response complete
    if (!lastMessage.isThinking && !lastMessage.isStreaming && responseText) {
      // Only announce if we haven't announced this message yet
      if (lastAnnouncedMessageId.current !== messageId) {
        lastAnnouncedMessageId.current = messageId;
        streamingAnnouncedRef.current = false;
        
        const announcement = buildCompleteAnnouncement(responseText, citations, followUps);
        setAnnouncementWithTimeout(announcement, 5000);
      }
    }
  }, [messages, srTexts, buildCompleteAnnouncement, setAnnouncementWithTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    ariaLiveMessage,
    announceCustomMessage,
  };
};

export default useScreenReaderAnnouncements;
